import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, X, ChevronRight, Tag as TagIcon, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function WatchlistPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState(null);
  const [editing, setEditing] = useState(null); // symbol being edited

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/watchlist"); setItems(data.items || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (sym) => {
    try {
      await api.delete(`/watchlist/${sym}`);
      setItems((prev) => prev.filter((i) => i.symbol !== sym));
      toast.success("Kaldırıldı");
    } catch { toast.error("Silinemedi"); }
  };

  const saveTags = async (sym, tags) => {
    try {
      await api.patch(`/watchlist/${sym}`, { tags });
      setItems((prev) => prev.map((i) => (i.symbol === sym ? { ...i, tags } : i)));
      toast.success("Etiketler kaydedildi");
      setEditing(null);
    } catch { toast.error("Kaydedilemedi"); }
  };

  const allTags = Array.from(new Set(items.flatMap((i) => i.tags || []))).sort();
  const filtered = activeTag ? items.filter((i) => (i.tags || []).includes(activeTag)) : items;

  return (
    <div className="space-y-6" data-testid="watchlist-page">
      <div>
        <h1 className="text-2xl font-semibold text-text-primary ztan-title">İzleme Listem</h1>
        <p className="text-sm text-text-secondary mt-1">Favori BIST hisseleriniz — etiketleyerek grup grup takip edin</p>
      </div>

      {allTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="watchlist-tag-filter">
          <span className="text-[10px] uppercase tracking-wider text-text-secondary flex items-center gap-1"><TagIcon size={11}/> Filtrele:</span>
          <button
            data-testid="watchlist-tag-all"
            onClick={() => setActiveTag(null)}
            className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
              !activeTag ? "bg-brand-blue text-white border-brand-blue" : "bg-bg-panel text-text-secondary border-bg-border hover:text-text-primary"
            }`}
          >Tümü ({items.length})</button>
          {allTags.map((t) => {
            const count = items.filter((i) => (i.tags || []).includes(t)).length;
            return (
              <button
                key={t}
                data-testid={`watchlist-tag-${t}`}
                onClick={() => setActiveTag(t)}
                className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                  activeTag === t
                    ? "bg-accent-yellow/15 text-accent-yellow border-accent-yellow/40"
                    : "bg-bg-panel text-text-secondary border-bg-border hover:text-text-primary"
                }`}
              >{t} ({count})</button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="p-16 flex justify-center"><div className="radar-loader" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-bg-panel border border-bg-border rounded-xl p-16 text-center">
          <Star size={32} className="text-text-secondary mx-auto mb-3" />
          <h3 className="text-text-primary font-semibold mb-1">
            {items.length === 0 ? "İzleme listeniz boş" : "Bu etikette hisse yok"}
          </h3>
          <p className="text-sm text-text-secondary">
            {items.length === 0 ? "Tarama sonuçlarından hisseleri yıldızlayarak buraya ekleyebilirsiniz." : "Farklı bir etiket seçin ya da hisse etiketleyin."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((it) => (
            <div
              key={it.symbol}
              data-testid={`watchlist-card-${it.symbol}`}
              className="bg-bg-panel border border-bg-border hover:border-brand-blue/60 rounded-xl p-5 group transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-lg font-mono font-bold text-text-primary tracking-wider">{it.symbol}</div>
                  <div className="text-xs text-text-secondary mt-0.5">{it.name}</div>
                </div>
                <button onClick={() => remove(it.symbol)} className="text-text-secondary hover:text-signal-down">
                  <X size={16} />
                </button>
              </div>

              {editing === it.symbol ? (
                <TagEditor
                  initial={it.tags || []}
                  suggestions={allTags}
                  onSave={(tags) => saveTags(it.symbol, tags)}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                <div className="mb-3 min-h-[24px] flex flex-wrap items-center gap-1.5">
                  {(it.tags || []).length === 0 ? (
                    <span className="text-[11px] text-text-secondary italic">Etiket yok</span>
                  ) : (it.tags || []).map((t) => (
                    <span key={t} className="text-[10px] px-2 py-0.5 rounded border bg-accent-yellow/10 text-accent-yellow border-accent-yellow/30">{t}</span>
                  ))}
                  <button
                    data-testid={`watchlist-edit-tags-${it.symbol}`}
                    onClick={() => setEditing(it.symbol)}
                    className="text-[11px] text-brand-blueLight hover:underline flex items-center gap-0.5 ml-1"
                  ><Plus size={10} /> etiket</button>
                </div>
              )}

              <Link
                to={`/stock/${it.symbol}`}
                className="bg-brand-blue text-white text-xs font-semibold py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 hover:bg-brand-blue/90"
              >
                Detay & Grafik <ChevronRight size={13} />
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TagEditor({ initial, suggestions, onSave, onCancel }) {
  const [tags, setTags] = useState(initial);
  const [input, setInput] = useState("");

  const add = (t) => {
    const clean = t.trim();
    if (!clean) return;
    if (!tags.includes(clean)) setTags([...tags, clean]);
    setInput("");
  };
  const removeTag = (t) => setTags(tags.filter((x) => x !== t));

  const suggestable = suggestions.filter((s) => !tags.includes(s) && s.toLowerCase().includes(input.toLowerCase())).slice(0, 5);

  return (
    <div className="mb-3 bg-bg-base border border-bg-border rounded-lg p-3 space-y-2" data-testid="tag-editor">
      <div className="flex flex-wrap gap-1.5">
        {tags.map((t) => (
          <span key={t} className="text-[10px] px-2 py-0.5 rounded border bg-accent-yellow/10 text-accent-yellow border-accent-yellow/30 flex items-center gap-1">
            {t}
            <button onClick={() => removeTag(t)} className="hover:opacity-70"><X size={10} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          data-testid="tag-editor-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(input); } }}
          placeholder="Etiket ekle (örn: Bankacılık)"
          className="flex-1 bg-bg-panel border border-bg-border text-text-primary text-xs rounded-md px-2 py-1.5 focus:border-brand-blue focus:outline-none"
        />
        <button
          data-testid="tag-editor-save"
          onClick={() => onSave(tags)}
          className="bg-brand-blue text-white text-xs px-3 py-1.5 rounded-md flex items-center gap-1 hover:bg-brand-blue/90"
        ><Check size={11} /> Kaydet</button>
        <button onClick={onCancel} className="text-text-secondary text-xs px-2">İptal</button>
      </div>
      {suggestable.length > 0 && (
        <div className="flex flex-wrap gap-1">
          <span className="text-[10px] text-text-secondary">Öneriler:</span>
          {suggestable.map((s) => (
            <button key={s} onClick={() => add(s)} className="text-[10px] text-brand-blueLight hover:underline">{s}</button>
          ))}
        </div>
      )}
    </div>
  );
}
